import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { REFRESH_EVENT } from '../lib/notificationActions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Users, DollarSign, Upload, Check, FileText, Clock, Calendar,
    Plus, X, TrendingUp, AlertCircle
} from 'lucide-react';
import EmailPreferencesPanel from '../components/EmailPreferencesPanel';

const TIME_OPTIONS = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '13:00', '13:30', '14:00',
    '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
];

export default function MedicalDashboard() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'schedule');
    const [appointments, setAppointments] = useState([]);
    const [availability, setAvailability] = useState([]);
    const [earnings, setEarnings] = useState({ transactions: [], total_earnings: 0, total_reports: 0 });
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(null);

    // Add availability state
    const [newDate, setNewDate] = useState('');
    const [selectedTimes, setSelectedTimes] = useState([]);
    const [addingSlots, setAddingSlots] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [apptRes, availRes, earningsRes] = await Promise.all([
                api.get('/medical/appointments'),
                api.get('/medical/availability'),
                api.get('/medical/earnings'),
            ]);
            setAppointments(apptRes.data.appointments);
            setAvailability(availRes.data.slots);
            setEarnings(earningsRes.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        const t = searchParams.get('tab');
        if (t && ['schedule', 'appointments', 'earnings'].includes(t)) setActiveTab(t);
    }, [searchParams]);

    useEffect(() => {
        const handler = (e) => {
            const keys = e.detail?.keys || [];
            if (keys.includes('appointments')) fetchData();
        };
        window.addEventListener(REFRESH_EVENT, handler);
        return () => window.removeEventListener(REFRESH_EVENT, handler);
    }, [fetchData]);

    const addAvailability = async () => {
        if (!newDate || selectedTimes.length === 0) return;
        setAddingSlots(true);
        try {
            await api.post('/medical/availability', { date: newDate, times: selectedTimes });
            setNewDate('');
            setSelectedTimes([]);
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setAddingSlots(false); }
    };

    const removeSlot = async (slotId) => {
        try {
            await api.delete(`/medical/availability/${slotId}`);
            await fetchData();
        } catch (err) { console.error(err); }
    };

    const uploadReport = async (apptId, file) => {
        setUploading(apptId);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/medical/appointments/${apptId}/upload-report`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setUploading(null); }
    };

    const toggleTime = (time) => {
        setSelectedTimes(prev => prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time]);
    };

    if (loading) {
        return <div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin" /></div>;
    }

    const confirmedAppts = appointments.filter(a => a.status === 'confirmed');
    const completedAppts = appointments.filter(a => a.status === 'completed');
    const unbookedSlots = availability.filter(s => !s.booked);
    const todayStr = new Date().toISOString().split('T')[0];

    // Group availability by date
    const slotsByDate = unbookedSlots.reduce((acc, s) => {
        (acc[s.date] = acc[s.date] || []).push(s);
        return acc;
    }, {});

    return (
        <div data-testid="medical-dashboard">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="border border-zinc-200 p-4" data-testid="stat-appointments">
                    <div className="flex items-center gap-2 mb-1 text-zinc-400">
                        <Calendar className="w-4 h-4" />
                        <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Appointments</span>
                    </div>
                    <p className="font-heading text-2xl font-black">{confirmedAppts.length}</p>
                </div>
                <div className="border border-zinc-200 p-4" data-testid="stat-completed">
                    <div className="flex items-center gap-2 mb-1 text-zinc-400">
                        <Check className="w-4 h-4" />
                        <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Completed</span>
                    </div>
                    <p className="font-heading text-2xl font-black">{completedAppts.length}</p>
                </div>
                <div className="border border-zinc-200 p-4" data-testid="stat-total-earnings">
                    <div className="flex items-center gap-2 mb-1 text-zinc-400">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Earnings</span>
                    </div>
                    <p className="font-heading text-2xl font-black">${earnings.total_earnings.toFixed(0)}</p>
                </div>
                <div className="border border-zinc-200 p-4" data-testid="stat-open-slots">
                    <div className="flex items-center gap-2 mb-1 text-zinc-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-[10px] tracking-[0.15em] uppercase font-bold">Open Slots</span>
                    </div>
                    <p className="font-heading text-2xl font-black">{unbookedSlots.length}</p>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-3 w-full max-w-md rounded-none h-12 bg-zinc-100 mb-6">
                    <TabsTrigger value="schedule" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="schedule-tab">
                        Schedule
                    </TabsTrigger>
                    <TabsTrigger value="appointments" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="appointments-tab">
                        Appointments ({confirmedAppts.length})
                    </TabsTrigger>
                    <TabsTrigger value="earnings" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="earnings-tab">
                        Earnings
                    </TabsTrigger>
                </TabsList>

                {/* Schedule Tab */}
                <TabsContent value="schedule">
                    {/* Add Slots */}
                    <div className="border border-zinc-200 p-6 mb-6" data-testid="add-availability-section">
                        <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                            <Plus className="w-5 h-5" /> Add Available Slots
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm font-semibold">Date</Label>
                                <Input
                                    type="date"
                                    value={newDate}
                                    onChange={e => setNewDate(e.target.value)}
                                    min={todayStr}
                                    className="mt-1.5 h-12 rounded-none max-w-xs"
                                    data-testid="availability-date-input"
                                />
                            </div>
                            <div>
                                <Label className="text-sm font-semibold">Time Slots</Label>
                                <div className="flex flex-wrap gap-2 mt-1.5">
                                    {TIME_OPTIONS.map(time => (
                                        <button
                                            key={time}
                                            type="button"
                                            onClick={() => toggleTime(time)}
                                            className={`px-3 py-2 text-sm border transition-colors ${
                                                selectedTimes.includes(time)
                                                    ? 'bg-zinc-900 text-white border-zinc-900'
                                                    : 'border-zinc-200 hover:border-zinc-400'
                                            }`}
                                            data-testid={`time-${time.replace(':', '')}`}
                                        >
                                            {time}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <Button
                                onClick={addAvailability}
                                disabled={addingSlots || !newDate || selectedTimes.length === 0}
                                className="h-12 rounded-none bg-zinc-900 text-white font-semibold px-8"
                                data-testid="add-slots-btn"
                            >
                                {addingSlots ? 'Adding...' : `Add ${selectedTimes.length} Slot${selectedTimes.length !== 1 ? 's' : ''}`}
                            </Button>
                        </div>
                    </div>

                    {/* Current Availability */}
                    <div>
                        <h3 className="font-heading text-sm font-bold text-zinc-400 mb-3">Your Available Slots</h3>
                        {Object.keys(slotsByDate).length === 0 ? (
                            <div className="text-center py-8 border border-zinc-200" data-testid="no-slots-state">
                                <Calendar className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
                                <p className="text-sm text-zinc-500">No available slots. Add some above.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {Object.entries(slotsByDate).sort().map(([date, slots]) => (
                                    <div key={date} className="border border-zinc-200 p-4" data-testid={`date-slots-${date}`}>
                                        <p className="text-sm font-semibold mb-2">
                                            {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {slots.sort((a, b) => a.time.localeCompare(b.time)).map(s => (
                                                <div key={s.id} className="flex items-center gap-1 border border-zinc-200 px-3 py-1.5 text-sm">
                                                    <span>{s.time}</span>
                                                    <button onClick={() => removeSlot(s.id)} className="text-zinc-400 hover:text-red-600 ml-1" data-testid={`remove-slot-${s.id}`}>
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>

                {/* Appointments Tab */}
                <TabsContent value="appointments">
                    {confirmedAppts.length === 0 && completedAppts.length === 0 ? (
                        <div className="text-center py-12 border border-zinc-200" data-testid="no-appointments-state">
                            <Users className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                            <p className="text-zinc-500">No appointments yet. Workers will book when you add availability.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {/* Confirmed appointments first */}
                            {confirmedAppts.map(appt => (
                                <div key={appt.id} className="border border-zinc-900 p-4" data-testid={`appt-${appt.id}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                                                <Users className="w-5 h-5 text-zinc-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-sm">{appt.worker_name}</p>
                                                <p className="text-xs text-zinc-500">{appt.worker_country} | {(appt.worker_skills || []).join(', ')}</p>
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="rounded-none text-xs">Confirmed</Badge>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-zinc-500 mb-3">
                                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {appt.date}</span>
                                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {appt.time}</span>
                                    </div>
                                    {!appt.has_report && (
                                        <div data-testid={`upload-report-${appt.id}`}>
                                            <input
                                                type="file"
                                                id={`report-${appt.id}`}
                                                className="hidden"
                                                accept=".pdf,image/*"
                                                onChange={e => e.target.files[0] && uploadReport(appt.id, e.target.files[0])}
                                                data-testid={`report-input-${appt.id}`}
                                            />
                                            <Button
                                                onClick={() => document.getElementById(`report-${appt.id}`).click()}
                                                variant="outline"
                                                className="w-full h-12 rounded-none border-zinc-900 font-semibold"
                                                disabled={uploading === appt.id}
                                                data-testid={`upload-report-btn-${appt.id}`}
                                            >
                                                {uploading === appt.id ? 'Uploading...' : (
                                                    <><Upload className="w-4 h-4 mr-2" /> Upload Medical Report</>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                    {appt.has_report && (
                                        <div className="flex items-center gap-2 text-green-600 text-sm">
                                            <Check className="w-4 h-4" /> Report uploaded
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Completed appointments */}
                            {completedAppts.length > 0 && (
                                <>
                                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mt-6 mb-2">Completed</p>
                                    {completedAppts.map(appt => (
                                        <div key={appt.id} className="border border-zinc-200 p-4 opacity-70" data-testid={`completed-appt-${appt.id}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-green-50 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-green-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-sm">{appt.worker_name}</p>
                                                        <p className="text-xs text-zinc-500">{appt.date} at {appt.time}</p>
                                                    </div>
                                                </div>
                                                <Badge className="rounded-none bg-green-100 text-green-800 border-green-200" variant="outline">Done</Badge>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </TabsContent>

                {/* Earnings Tab */}
                <TabsContent value="earnings">
                    <div className="border border-zinc-200 p-6 mb-6" data-testid="earnings-summary">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="w-5 h-5" />
                            <h3 className="font-heading text-lg font-bold">Earnings Summary</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">Fee per Report</p>
                                <p className="font-heading text-xl font-bold">$120.00</p>
                            </div>
                            <div>
                                <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">Your Share (60%)</p>
                                <p className="font-heading text-xl font-bold">$72.00</p>
                            </div>
                        </div>
                    </div>

                    {earnings.transactions.length > 0 ? (
                        <div className="border border-zinc-200 overflow-x-auto" data-testid="earnings-table">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-bold">Worker</TableHead>
                                        <TableHead className="font-bold">Amount</TableHead>
                                        <TableHead className="font-bold">Your Share</TableHead>
                                        <TableHead className="font-bold">Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {earnings.transactions.map(t => (
                                        <TableRow key={t.id}>
                                            <TableCell className="text-sm">{t.worker_name || 'Worker'}</TableCell>
                                            <TableCell className="text-sm">${t.amount}</TableCell>
                                            <TableCell className="text-sm font-semibold">${t.partner_payout}</TableCell>
                                            <TableCell className="text-sm text-zinc-500">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-8 border border-zinc-200">
                            <DollarSign className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                            <p className="text-zinc-500">No transactions yet</p>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Email Preferences — opt out of categories of transactional emails. */}
            <div className="mt-8">
                <EmailPreferencesPanel />
            </div>
        </div>
    );
}
